import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, styled } from '@mui/material';
import { ReactNode } from 'react';
import { Thumbnail } from '../../Thumbnail';
import { ImageEditorWorkspace, useImageEditorActions } from './ImageEditorWorkspace';
import { useImageEditor } from './useImageEditor';
import { DIALOG_MAX_WIDTH_BREAKPOINT, useEditorCanvasBounds } from './useEditorCanvasBounds';

type ImageEditorModalProperties = {
  open: boolean;
  files: File[];
  onComplete: (editedFiles: File[]) => void;
  onCancel: () => void;
  submitLabel?: string;
  children?: ReactNode;
};

const FILMSTRIP_THUMB_SIZE = 48;

const FilmstripItem = styled(Box, { shouldForwardProp: property => property !== 'isActive' })<{ isActive: boolean }>(
  ({ theme, isActive }) => ({
    width: FILMSTRIP_THUMB_SIZE,
    height: FILMSTRIP_THUMB_SIZE,
    borderRadius: '8px',
    overflow: 'hidden',
    cursor: 'pointer',
    border: isActive ? `2px solid ${theme.palette.primary.main}` : '2px solid transparent',
    opacity: isActive ? 1 : 0.6,
    transition: 'opacity 0.15s, border-color 0.15s',
    '&:hover': { opacity: 1 },
    flexShrink: 0,
  })
);

export const ImageEditorModal = ({
  open,
  files,
  onComplete,
  onCancel,
  submitLabel = 'Search',
  children,
}: ImageEditorModalProperties) => {
  const editor = useImageEditor(files);
  const { currentIndex, setCurrentIndex, imageStates, currentState, resetCurrentImage, getOutputFiles } = editor;
  const actions = useImageEditorActions(editor);
  const canvasBounds = useEditorCanvasBounds(files.length > 1);

  if (!open || !currentState) return;

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth={DIALOG_MAX_WIDTH_BREAKPOINT}
      fullWidth
      slotProps={{
        paper: {
          sx: { borderRadius: '12px', minHeight: 'min(500px, calc(100% - 64px))', userSelect: 'none' },
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>Edit Image</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
        <ImageEditorWorkspace editor={editor} actions={actions} bounds={canvasBounds} />

        {files.length > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, flexWrap: 'wrap' }}>
            {imageStates.map((state, index) => {
              const handleFilmstripClick = () => {
                setCurrentIndex(index);
                if (state.activeTool === 'smartCrop' && !state.cropRect) {
                  actions.autoDetectCrop(state.originalFile);
                }
              };
              return (
                <FilmstripItem
                  key={`${state.originalFile.name}-${index}`}
                  isActive={index === currentIndex}
                  onClick={handleFilmstripClick}
                >
                  <Thumbnail file={state.editedFile ?? state.originalFile} size={FILMSTRIP_THUMB_SIZE} noLink />
                </FilmstripItem>
              );
            })}
          </Box>
        )}
        {children}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button onClick={onCancel} sx={{ textTransform: 'none', borderRadius: '12px' }}>
          Cancel
        </Button>
        <Button onClick={resetCurrentImage} sx={{ textTransform: 'none', borderRadius: '12px' }}>
          Reset
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button
          variant='contained'
          onClick={() => onComplete(getOutputFiles())}
          sx={{ textTransform: 'none', borderRadius: '12px' }}
        >
          {submitLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
